// KinsCircle global translation dictionary.
//
// HOW IT WORKS:
//   - Each key is the canonical English source string (used as a stable id).
//   - Each language map provides the translated equivalent.
//   - Missing keys gracefully fall back to the English source string,
//     so a half-translated UI never shows blank or broken text.
//
// HOW TO ADD A LANGUAGE:
//   1. Add the language code (ISO 639-1) to SUPPORTED_LANGUAGES below.
//   2. Add an entry under `translations` keyed by that code.
//   3. Provide translated values for the keys you care about; missing ones
//      will fall through to English automatically.
//
// IMPORTANT: never translate user-generated content (names, bios, memories,
// photos, comments). The `t()` function only translates strings that are
// explicitly looked up — plain rendered values like `{user.name}` are
// untouched by design.

export type LanguageCode = 'en' | 'es' | 'fr' | 'de';

export interface LanguageMeta {
  code: LanguageCode;
  label: string; // English label
  native: string; // native label, shown in the dropdown
  flag: string; // small emoji-free identifier (we use 2-letter code)
}

export const SUPPORTED_LANGUAGES: LanguageMeta[] = [
  { code: 'en', label: 'English', native: 'English', flag: 'EN' },
  { code: 'es', label: 'Spanish', native: 'Español', flag: 'ES' },
  { code: 'fr', label: 'French', native: 'Français', flag: 'FR' },
  { code: 'de', label: 'German', native: 'Deutsch', flag: 'DE' },
];

export const DEFAULT_LANGUAGE: LanguageCode = 'en';

// Translation dictionaries. Keys are English source strings.
//
// We deliberately keep `en` empty so the source string itself is the value —
// this also serves as the canonical "key list" that other languages mirror.
type Dict = Record<string, string>;

const es: Dict = {
  // Navigation
  'Home': 'Inicio',
  'Features': 'Funciones',
  'Pricing': 'Precios',
  'About': 'Acerca de',
  'Blog': 'Blog',
  'Contact Support': 'Contactar soporte',

  // Auth / account
  'Log In': 'Iniciar sesión',
  'Sign Up': 'Registrarse',
  'Log Out': 'Cerrar sesión',
  'Sign out': 'Cerrar sesión',
  'Get Started': 'Comenzar',
  'Account': 'Cuenta',
  'Account settings': 'Configuración de cuenta',
  'Upgrade': 'Mejorar plan',
  'Email Settings': 'Ajustes de correo',
  'Email Settings (SMTP)': 'Ajustes de correo (SMTP)',

  // Header CTAs
  'Start Your Family': 'Comienza tu familia',
  'Start Your Family Tree': 'Crea tu árbol familiar',
  'Share KinsCircle': 'Compartir KinsCircle',
  'Invite family': 'Invitar familia',
  'Invite Family': 'Invitar familia',
  'Messages': 'Mensajes',
  'Public invitation': 'Invitación pública',
  'Invite Anyone to Build Their Family': 'Invita a cualquiera a crear su familia',
  'Anyone can start their own KinsCircle in minutes — no invite link required. Share the public page and help more families connect.':
    'Cualquiera puede comenzar su propio KinsCircle en minutos — sin necesidad de enlace de invitación. Comparte la página pública y ayuda a conectar más familias.',
  '3-Day Free Trial · Card Required · Cancel Anytime':
    '3 días de prueba gratis · Tarjeta requerida · Cancela cuando quieras',



  // Account page
  'Manage your profile, billing, and security.': 'Administra tu perfil, facturación y seguridad.',
  'Plan & role': 'Plan y rol',
  'Your current access level on KinsCircle.': 'Tu nivel de acceso actual en KinsCircle.',
  'Plan': 'Plan',
  'Role': 'Rol',
  'Profile': 'Perfil',
  'Your display name and family name.': 'Tu nombre visible y nombre de familia.',
  'Your name': 'Tu nombre',
  'Family name': 'Nombre de familia',
  'Save profile': 'Guardar perfil',
  'Saving…': 'Guardando…',
  'Email address': 'Dirección de correo',
  'Email': 'Correo',
  'Update email': 'Actualizar correo',
  'Updating…': 'Actualizando…',
  'Password': 'Contraseña',
  'Change the password used to sign in.': 'Cambia la contraseña para iniciar sesión.',
  'New password': 'Nueva contraseña',
  'Confirm new password': 'Confirmar nueva contraseña',
  'Update password': 'Actualizar contraseña',
  'Billing & subscription': 'Facturación y suscripción',
  'Manage billing': 'Gestionar facturación',
  'See plans': 'Ver planes',
  'Privacy, Safety & Terms': 'Privacidad, seguridad y términos',
  'Open Privacy & Safety': 'Abrir Privacidad y seguridad',
  'Open Terms of Service': 'Abrir Términos de servicio',
  'Danger zone': 'Zona de peligro',
  'Delete account': 'Eliminar cuenta',
  'Back': 'Volver',
  'Back to home': 'Volver al inicio',
  'Sign in required': 'Necesitas iniciar sesión',
  'You need to be logged in to view account settings.': 'Necesitas iniciar sesión para ver los ajustes de cuenta.',

  // Language selector
  'Language': 'Idioma',
  'Choose the language used across the app.': 'Elige el idioma usado en toda la aplicación.',
  'Select a language': 'Selecciona un idioma',

  // Common
  'Cancel': 'Cancelar',
  'Save': 'Guardar',
  'Close': 'Cerrar',
  'Loading…': 'Cargando…',
  'Search': 'Buscar',
  'Copy Link': 'Copiar enlace',
  'WhatsApp': 'WhatsApp',
  'SMS': 'SMS',
};

const fr: Dict = {
  'Home': 'Accueil',
  'Features': 'Fonctionnalités',
  'Pricing': 'Tarifs',
  'About': 'À propos',
  'Blog': 'Blog',
  'Contact Support': 'Contacter le support',

  'Log In': 'Connexion',
  'Sign Up': "S'inscrire",
  'Log Out': 'Déconnexion',
  'Sign out': 'Se déconnecter',
  'Get Started': 'Commencer',
  'Account': 'Compte',
  'Account settings': 'Paramètres du compte',
  'Upgrade': 'Mettre à niveau',
  'Email Settings': 'Paramètres e-mail',
  'Email Settings (SMTP)': 'Paramètres e-mail (SMTP)',

  'Start Your Family': 'Commencer votre famille',
  'Start Your Family Tree': 'Créer votre arbre familial',
  'Share KinsCircle': 'Partager KinsCircle',
  'Invite family': 'Inviter la famille',
  'Invite Family': 'Inviter la famille',
  'Messages': 'Messages',
  'Public invitation': 'Invitation publique',
  'Invite Anyone to Build Their Family': 'Invitez tout le monde à créer sa famille',
  'Anyone can start their own KinsCircle in minutes — no invite link required. Share the public page and help more families connect.':
    'Tout le monde peut créer son propre KinsCircle en quelques minutes — aucun lien d\'invitation requis. Partagez la page publique et aidez davantage de familles à se connecter.',
  '3-Day Free Trial · Card Required · Cancel Anytime':
    "Essai gratuit de 3 jours · Carte requise · Annulez à tout moment",




  'Manage your profile, billing, and security.': 'Gérez votre profil, votre facturation et votre sécurité.',
  'Plan & role': 'Forfait et rôle',
  'Your current access level on KinsCircle.': "Votre niveau d'accès actuel sur KinsCircle.",
  'Plan': 'Forfait',
  'Role': 'Rôle',
  'Profile': 'Profil',
  'Your display name and family name.': "Votre nom d'affichage et nom de famille.",
  'Your name': 'Votre nom',
  'Family name': 'Nom de famille',
  'Save profile': 'Enregistrer le profil',
  'Saving…': 'Enregistrement…',
  'Email address': 'Adresse e-mail',
  'Email': 'E-mail',
  'Update email': "Mettre à jour l'e-mail",
  'Updating…': 'Mise à jour…',
  'Password': 'Mot de passe',
  'Change the password used to sign in.': 'Modifier le mot de passe utilisé pour la connexion.',
  'New password': 'Nouveau mot de passe',
  'Confirm new password': 'Confirmer le nouveau mot de passe',
  'Update password': 'Mettre à jour le mot de passe',
  'Billing & subscription': 'Facturation et abonnement',
  'Manage billing': 'Gérer la facturation',
  'See plans': 'Voir les forfaits',
  'Privacy, Safety & Terms': 'Confidentialité, sécurité et conditions',
  'Open Privacy & Safety': 'Ouvrir Confidentialité et sécurité',
  'Open Terms of Service': "Ouvrir les conditions d'utilisation",
  'Danger zone': 'Zone de danger',
  'Delete account': 'Supprimer le compte',
  'Back': 'Retour',
  'Back to home': "Retour à l'accueil",
  'Sign in required': 'Connexion requise',
  'You need to be logged in to view account settings.': 'Vous devez être connecté pour voir les paramètres du compte.',

  'Language': 'Langue',
  'Choose the language used across the app.': "Choisissez la langue utilisée dans toute l'application.",
  'Select a language': 'Sélectionner une langue',

  'Cancel': 'Annuler',
  'Save': 'Enregistrer',
  'Close': 'Fermer',
  'Loading…': 'Chargement…',
  'Search': 'Rechercher',
  'Copy Link': 'Copier le lien',
  'WhatsApp': 'WhatsApp',
  'SMS': 'SMS',
};

const de: Dict = {
  'Home': 'Startseite',
  'Features': 'Funktionen',
  'Pricing': 'Preise',
  'About': 'Über uns',
  'Blog': 'Blog',
  'Contact Support': 'Support kontaktieren',

  'Log In': 'Anmelden',
  'Sign Up': 'Registrieren',
  'Log Out': 'Abmelden',
  'Sign out': 'Abmelden',
  'Get Started': 'Loslegen',
  'Account': 'Konto',
  'Account settings': 'Kontoeinstellungen',
  'Upgrade': 'Upgrade',
  'Email Settings': 'E-Mail-Einstellungen',
  'Email Settings (SMTP)': 'E-Mail-Einstellungen (SMTP)',
  'Start Your Family': 'Familie starten',
  'Start Your Family Tree': 'Familienstammbaum erstellen',
  'Share KinsCircle': 'KinsCircle teilen',
  'Invite family': 'Familie einladen',
  'Invite Family': 'Familie einladen',
  'Messages': 'Nachrichten',
  'Public invitation': 'Öffentliche Einladung',
  'Invite Anyone to Build Their Family': 'Lade jeden ein, seine Familie aufzubauen',
  'Anyone can start their own KinsCircle in minutes — no invite link required. Share the public page and help more families connect.':
    'Jeder kann seinen eigenen KinsCircle in wenigen Minuten starten — kein Einladungslink erforderlich. Teilen Sie die öffentliche Seite und helfen Sie weiteren Familien, sich zu verbinden.',
  '3-Day Free Trial · Card Required · Cancel Anytime':
    '3 Tage kostenlos testen · Karte erforderlich · Jederzeit kündbar',



  'Manage your profile, billing, and security.': 'Verwalten Sie Ihr Profil, Ihre Abrechnung und Sicherheit.',
  'Plan & role': 'Tarif & Rolle',
  'Your current access level on KinsCircle.': 'Ihre aktuelle Zugriffsebene auf KinsCircle.',
  'Plan': 'Tarif',
  'Role': 'Rolle',
  'Profile': 'Profil',
  'Your display name and family name.': 'Ihr Anzeigename und Familienname.',
  'Your name': 'Ihr Name',
  'Family name': 'Familienname',
  'Save profile': 'Profil speichern',
  'Saving…': 'Wird gespeichert…',
  'Email address': 'E-Mail-Adresse',
  'Email': 'E-Mail',
  'Update email': 'E-Mail aktualisieren',
  'Updating…': 'Wird aktualisiert…',
  'Password': 'Passwort',
  'Change the password used to sign in.': 'Ändern Sie das Passwort für die Anmeldung.',
  'New password': 'Neues Passwort',
  'Confirm new password': 'Neues Passwort bestätigen',
  'Update password': 'Passwort aktualisieren',
  'Billing & subscription': 'Abrechnung & Abonnement',
  'Manage billing': 'Abrechnung verwalten',
  'See plans': 'Tarife ansehen',
  'Privacy, Safety & Terms': 'Datenschutz, Sicherheit & AGB',
  'Open Privacy & Safety': 'Datenschutz & Sicherheit öffnen',
  'Open Terms of Service': 'Nutzungsbedingungen öffnen',
  'Danger zone': 'Gefahrenzone',
  'Delete account': 'Konto löschen',
  'Back': 'Zurück',
  'Back to home': 'Zurück zur Startseite',
  'Sign in required': 'Anmeldung erforderlich',
  'You need to be logged in to view account settings.': 'Sie müssen angemeldet sein, um die Kontoeinstellungen zu sehen.',

  'Language': 'Sprache',
  'Choose the language used across the app.': 'Wählen Sie die in der App verwendete Sprache.',
  'Select a language': 'Sprache auswählen',

  'Cancel': 'Abbrechen',
  'Save': 'Speichern',
  'Close': 'Schließen',
  'Loading…': 'Wird geladen…',
  'Search': 'Suchen',
  'Copy Link': 'Link kopieren',
  'WhatsApp': 'WhatsApp',
  'SMS': 'SMS',
};

export const translations: Record<LanguageCode, Dict> = {
  en: {}, // English uses source strings as-is
  es,
  fr,
  de,
};

/**
 * Look up a translated string for the given language. Falls back gracefully:
 *   1. exact match in target dictionary
 *   2. the English source string (the key itself)
 */
export function translate(
  key: string,
  lang: LanguageCode,
  fallback?: string
): string {
  if (!key) return fallback ?? '';
  if (lang === 'en') return fallback ?? key;
  const dict = translations[lang];
  if (dict && dict[key]) return dict[key];
  return fallback ?? key;
}

/**
 * Detect a supported language from the browser's navigator.language.
 * Returns DEFAULT_LANGUAGE if the browser language isn't in the supported list.
 */
export function detectBrowserLanguage(): LanguageCode {
  if (typeof navigator === 'undefined') return DEFAULT_LANGUAGE;
  const raw = (navigator.language || (navigator as any).userLanguage || '').toLowerCase();
  if (!raw) return DEFAULT_LANGUAGE;
  const short = raw.split('-')[0] as LanguageCode;
  const supported = SUPPORTED_LANGUAGES.find((l) => l.code === short);
  return supported ? supported.code : DEFAULT_LANGUAGE;
}
